import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';

import { getTestId } from '../../utils/testUtils';
import type { Flow, Question, QuestionFlowItemSummary, ResponsePayload } from '../../types';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ChevronLeft, Loader2, Volume2, VolumeOff } from 'lucide-react';
import QuestionCard from '../QuestionCard';
import { QuestionFeedbackButton } from './QuestionFeedbackButton';
import { GenerateNoteFromFlowButton } from './GenerateNoteFromFlowButton';
import { FlowCompleted } from './FlowCompleted';
import type { SubmitQuestionOptions } from './useQuestionFlowManager';
import { DebugPanel, DebugField } from '../Debug/DebugPanel';
import type { FlowCompletionContext } from './types';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';
import { useQuestionNarration } from './useQuestionNarration';
import { INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';
import { getFlowThemeCardMotion, resolveFlowTheme } from './themes';
import { AnimatePresence } from '../ui/safe-animate-presence';
import { useOptionalThemeContext } from './themes/DynamicThemeContext';
import { hasAnswerEvaluation } from 'utils/answerEvaluation';
import { AiEvaluatedQuestion } from './AiEvaluatedQuestion';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { hasAnswerReferences } from './useAnswerReferences';
import { QuestionLoadingSkeleton } from './QuestionLoadingSkeleton';

const QUESTION_CARD_VARIANTS = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? '100%' : '-100%',
  }),
  center: {
    x: 0,
    filter: 'none',
    transitionEnd: { filter: 'none' },
  },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? '-100%' : '100%',
  }),
};

const QUESTION_CARD_MOBILE_VARIANTS = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? 'calc(100% + 1.5rem)' : 'calc(-100% - 1.5rem)',
  }),
  center: {
    x: 0,
    filter: 'none',
    transitionEnd: { filter: 'none' },
  },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? 'calc(-100% - 1.5rem)' : 'calc(100% + 1.5rem)',
  }),
};

const QUESTION_CARD_TRANSITION = {
  duration: 0.5,
  ease: [0.22, 0.61, 0.36, 1],
} as const;

const FLOW_COMPLETED_VARIANTS = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    y: 12,
  }),
  center: {
    x: 0,
    opacity: 1,
    y: 0,
    filter: 'none',
    transitionEnd: { filter: 'none' },
  },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
    y: -12,
  }),
};

const FLOW_COMPLETED_TRANSITION = {
  duration: 0.5,
  ease: [0.17, 0.67, 0.35, 1],
} as const;

// Switching preferences can interrupt any theme's arrival. Reset all transform
// channels used by card motions so a previous scale/rotation cannot survive.
const REDUCED_MOTION_TARGET = { x: 0, y: 0, rotate: 0, rotateY: 0, scale: 1, opacity: 1, filter: 'none' };
const REDUCED_MOTION_CENTER = { ...REDUCED_MOTION_TARGET, transition: { duration: 0 }, transitionEnd: { filter: 'none' } };
const REDUCED_MOTION_VARIANTS = {
  enter: REDUCED_MOTION_TARGET,
  center: REDUCED_MOTION_CENTER,
  exit: REDUCED_MOTION_TARGET,
};
const REDUCED_MOTION_TRANSITION = { duration: 0 } as const;

const EDGE_FADE_MASK =
  'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 88%, rgba(0,0,0,0) 100%)';
const EDGE_FADE_MASK_MOBILE =
  'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 2%, rgba(0,0,0,1) 98%, rgba(0,0,0,0) 100%)';
const QUESTION_STAGE_OFFSET_PX = 180;
const QUESTION_STAGE_MIN_HEIGHT_CLASS = 'min-h-[calc(100dvh-180px)] md:min-h-[calc(100vh-180px)]';
// The standard stage replaces fixed vertical padding with flexible spacers: a
// short card stays centred, while a long card rises to a small top gap instead
// of starting below the padding. On phones the Previous control is a small tab
// on the card's top edge, so the top gap only has to leave room for that tab.
const QUESTION_STAGE_FLEX_OFFSET_PX = 84;
const SWIPE_TRIGGER_DISTANCE = 80;
const SWIPE_MAX_VERTICAL_DRIFT = 48;

interface DebugMetadata {
  flowId: number | null;
  flowName: string | null;
  flowFrequency: string | null;
  flowRunId: number | null;
  flowStatus: string | null;
  flowCompletedAt: string | null;
  progressIndex: number | null;
  totalQuestions: number | null;
  questionStep: number | null;
  questionIndex: number | null;
  questionId: number | null;
  questionKey: string | null;
  questionTypeDisplay: string | null;
  questionType: string | null;
  flowItemId: number | null;
  isSubmitting: boolean;
  hasUnsavedChanges: boolean;
  draft: ResponsePayload | null;
}

interface QuestionFlowStageProps {
  isAtFlowEnd: boolean;
  transitionDirection: 1 | -1;
  question: Question | null;
  generationId?: number;
  questionLoading?: boolean;
  questionError?: string;
  submissionError?: string;
  onRetryQuestion?: () => void;
  recoveryContent?: React.ReactNode;
  onQuestionDraftChange?: (questionId: number, draft: ResponsePayload | null) => void;
  onGenerationConflict?: (draft: ResponsePayload | null, reply?: string) => void;
  questionResponse?: ResponsePayload;
  questionFlowItem?: QuestionFlowItemSummary;
  isSubmitting: boolean;
  isSavingResponse?: boolean;
  isLastQuestion: boolean;
  canGoToPrevious: boolean;
  onPrevious: () => void;
  onQuestionSubmit: (response: ResponsePayload, options?: SubmitQuestionOptions) => void;
  onServerResponses?: (responses: unknown[]) => void;
  flow: Flow | null;
  courseId?: number | null;
  flowRunId: number | null;
  currentQuestionIndex: number;
  questionIndex: number;
  upcomingQuestionIds?: number[];
  debugMetadata: DebugMetadata | null;
  completionContext: FlowCompletionContext;
  lastQuestionButtonText?: string;
  topRightAccessory?: React.ReactNode;
  bottomRightAccessory?: React.ReactNode;
  /** Omit account-backed tools in isolated, sample-only previews. */
  showAccountTools?: boolean;
  /** Presentational completion override; avoids learner completion/API behavior. */
  completionContent?: React.ReactNode;
}

export const QuestionFlowStage: React.FC<QuestionFlowStageProps> = ({
  isAtFlowEnd,
  transitionDirection,
  question,
  generationId,
  questionLoading = false,
  questionError,
  submissionError,
  onRetryQuestion,
  recoveryContent,
  onQuestionDraftChange,
  onGenerationConflict,
  questionResponse,
  questionFlowItem,
  isSubmitting,
  isSavingResponse = false,
  isLastQuestion,
  canGoToPrevious,
  onPrevious,
  onQuestionSubmit,
  onServerResponses,
  flow,
  courseId = null,
  flowRunId,
  currentQuestionIndex,
  questionIndex,
  upcomingQuestionIds = [],
  debugMetadata,
  completionContext,
  lastQuestionButtonText,
  topRightAccessory,
  bottomRightAccessory,
  showAccountTools = true,
  completionContent,
}) => {
  const [evaluationPending, setEvaluationPending] = useState(false);
  const isFormLocked = isSubmitting || isSavingResponse;
  const isInteractionLocked = isFormLocked || evaluationPending;
  const QuestionComponent = !flow?.is_quiz && hasAnswerEvaluation(question) ? AiEvaluatedQuestion : QuestionCard;
  const { isMobile } = useResponsiveLayout();
  const legacyReducedMotion = useReducedMotion();
  const themeContext = useOptionalThemeContext();
  // The shared context follows live OS and saved preferences. Framer's hook in
  // the supported version only reads its initial preference, so keep it solely
  // as a fallback for callers that do not supply a theme context.
  const prefersReducedMotion = themeContext?.preferences.reducedMotion ?? legacyReducedMotion;
  const reducedMotionControls = useAnimationControls();
  const narration = useQuestionNarration({
    // Shared narration audio is cached per question, so learner-specific wording
    // (personalized text or answer references) is never read through it.
    question: questionLoading || questionError || recoveryContent || hasQuestionGeneration(question)
      || hasAnswerReferences(question) ? null : question,
    upcomingQuestionIds,
  });
  const stageContentRef = useRef<HTMLDivElement | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const questionId = question?.id ?? null;
  const handleDraftChange = useCallback((draft: ResponsePayload | null) => {
    if (questionId != null) onQuestionDraftChange?.(questionId, draft);
  }, [onQuestionDraftChange, questionId]);
  const questionKey = question?.key ?? currentQuestionIndex;
  const renderedStageKey = isAtFlowEnd
    ? 'flow-completed'
    : question
      ? `question-${questionId ?? questionKey}`
      : null;
  useEffect(() => {
    if (!prefersReducedMotion) return;
    // A changed duration does not restart tweens whose final targets are equal.
    // Stop and set the subscribed card so live preferences interrupt immediately.
    reducedMotionControls.stop();
    reducedMotionControls.set(REDUCED_MOTION_TARGET);
  }, [prefersReducedMotion, reducedMotionControls, renderedStageKey]);
  const previousRenderedStageKeyRef = useRef<string | null>(null);
  const rootScrollTopRef = useRef(0);
  const resolvedEdgeMask = isMobile ? EDGE_FADE_MASK_MOBILE : EDGE_FADE_MASK;
  const flowTheme = flow?.theme;
  const themeCardMotion = useMemo(
    () => (prefersReducedMotion ? null : getFlowThemeCardMotion(resolveFlowTheme(flowTheme).id, isMobile)),
    [flowTheme, isMobile, prefersReducedMotion],
  );
  const resolvedQuestionCardVariants = prefersReducedMotion
    ? REDUCED_MOTION_VARIANTS
    : themeCardMotion
      ? themeCardMotion.variants
      : isMobile
        ? QUESTION_CARD_MOBILE_VARIANTS
        : QUESTION_CARD_VARIANTS;
  const resolvedQuestionCardTransition = prefersReducedMotion
    ? REDUCED_MOTION_TRANSITION
    : themeCardMotion?.transition ?? QUESTION_CARD_TRANSITION;
  const usesCompactMobileQuizLayout = Boolean(flow?.is_quiz) && !isAtFlowEnd;
  const usesCompactMobileIntakeLayout =
    flow?.name === INTAKE_ASSESSMENT_FLOW_NAME && !isAtFlowEnd;
  const usesCompactMobileLayout =
    usesCompactMobileQuizLayout || usesCompactMobileIntakeLayout;
  const showNarrationControls =
    !isAtFlowEnd &&
    Boolean(question) &&
    !hasQuestionGeneration(question) &&
    !hasAnswerReferences(question) &&
    !recoveryContent &&
    narration.isAvailable &&
    narration.canNarrateCurrentQuestion;
  // Reserve the same mobile footer space for the visible card before and after
  // completion. Padding only the question wrapper shifts its visual centre by
  // half that padding (64px on phones), causing a jump to the completion card.
  const mobileCardBottomPadding = Boolean(flow?.is_quiz) || flow?.name === INTAKE_ASSESSMENT_FLOW_NAME
    ? 'pb-[calc(env(safe-area-inset-bottom)+4.5rem)] md:pb-0'
    : 'pb-[calc(env(safe-area-inset-bottom)+8rem)] md:pb-0';
  const usesFlexibleStageGap = !usesCompactMobileLayout;
  // Phones show Previous as a tab on the card instead of a floating circle above it.
  const usesCardBackTab = isMobile && !usesCompactMobileLayout;
  const questionsContainerClassName = [
    `relative mx-auto flex w-full justify-center px-1 ${QUESTION_STAGE_MIN_HEIGHT_CLASS}`,
    usesCompactMobileLayout
      ? usesCompactMobileIntakeLayout
        ? 'items-start py-2 md:items-center md:py-12'
        : 'items-start py-3 md:items-center md:py-12'
      : 'items-start',
  ].join(' ');

  useLayoutEffect(() => {
    const scrollContainer = document.getElementById('root');
    if (!scrollContainer) {
      return undefined;
    }

    const trackScrollPosition = () => {
      rootScrollTopRef.current = scrollContainer.scrollTop;
    };

    trackScrollPosition();
    scrollContainer.addEventListener('scroll', trackScrollPosition, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', trackScrollPosition);
    };
  }, []);

  useLayoutEffect(() => {
    if (renderedStageKey === null) {
      return;
    }

    const previousRenderedStageKey = previousRenderedStageKeyRef.current;
    previousRenderedStageKeyRef.current = renderedStageKey;

    if (previousRenderedStageKey === null || previousRenderedStageKey === renderedStageKey) {
      return;
    }

    const scrollContainer = document.getElementById('root');
    if (!scrollContainer) {
      return;
    }

    const outgoingStage = Array.from(
      stageContentRef.current?.querySelectorAll<HTMLElement>('[data-flow-stage-key]') ?? [],
    ).find((stage) => stage.dataset.flowStageKey === previousRenderedStageKey);
    const scrollCompensation = outgoingStage?.querySelector<HTMLElement>(
      '[data-flow-stage-scroll-compensation]',
    );
    const outgoingScrollTop = rootScrollTopRef.current;

    if (scrollCompensation && outgoingScrollTop > 0) {
      // Keep the outgoing viewport slice stationary when #root resets below.
      scrollCompensation.style.transform = `translateY(-${outgoingScrollTop}px)`;
    }

    scrollContainer.scrollTop = 0;
    rootScrollTopRef.current = 0;
  }, [renderedStageKey]);

  const isSwipeIgnoredTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      target.closest(
        'input, textarea, select, button, a, [role="button"], [contenteditable="true"], [data-swipe-ignore="true"], .video-js',
      ),
    );
  }, []);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobile || isAtFlowEnd || event.touches.length !== 1) {
        touchStartRef.current = null;
        return;
      }

      if (isSwipeIgnoredTarget(event.target)) {
        touchStartRef.current = null;
        return;
      }

      const touch = event.touches[0];
      if (!touch) {
        touchStartRef.current = null;
        return;
      }
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    },
    [isAtFlowEnd, isMobile, isSwipeIgnoredTarget],
  );

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobile || isAtFlowEnd || isInteractionLocked || event.changedTouches.length !== 1) {
        touchStartRef.current = null;
        return;
      }

      const startPoint = touchStartRef.current;
      touchStartRef.current = null;
      if (!startPoint) {
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) {
        return;
      }

      const deltaX = touch.clientX - startPoint.x;
      const deltaY = touch.clientY - startPoint.y;
      if (Math.abs(deltaY) > SWIPE_MAX_VERTICAL_DRIFT) {
        return;
      }

      if (deltaX >= SWIPE_TRIGGER_DISTANCE) {
        if (canGoToPrevious) {
          onPrevious();
        }
        return;
      }

      if (deltaX <= -SWIPE_TRIGGER_DISTANCE) {
        const submitButton = stageContentRef.current?.querySelector<HTMLButtonElement>(
          '[data-testid="submit-button"]',
        );
        if (submitButton && !submitButton.disabled) {
          submitButton.click();
        }
      }
    },
    [canGoToPrevious, isAtFlowEnd, isInteractionLocked, isMobile, onPrevious],
  );

  return (
    <>
      <div
        className={questionsContainerClassName}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        {...getTestId('flow-questions-container')}
      >
        {!isAtFlowEnd && topRightAccessory ? (
          <div className="absolute right-2 top-2 z-20 flex max-w-[min(16rem,calc(100%-1rem))] flex-col items-end gap-2 sm:right-4 sm:top-4">
            {topRightAccessory}
          </div>
        ) : null}

        {canGoToPrevious && !usesCardBackTab && (
          <Button
            variant="secondary"
            onClick={onPrevious}
            disabled={isInteractionLocked}
            type="button"
            size="icon"
            className={`absolute left-2 top-2 z-20 rounded-full p-0 md:fixed md:left-10 md:top-1/2 md:z-30 md:h-12 md:w-12 md:-translate-y-1/2 ${
              usesCompactMobileIntakeLayout ? 'h-8 w-8' : 'h-10 w-10'
            }`}
            aria-label="Previous question"
          >
            <ChevronLeft
              aria-hidden="true"
              className={usesCompactMobileIntakeLayout ? 'h-4 w-4 md:h-6 md:w-6' : 'h-6 w-6'}
            />
            <span className="sr-only">Previous question</span>
          </Button>
        )}

        <div
          ref={stageContentRef}
          data-testid="flow-stage-content"
          className={`relative h-full w-full overflow-x-clip overflow-y-visible ${
            usesCompactMobileIntakeLayout
              ? 'grid place-items-center pb-2 pt-12 md:py-12'
              : usesCompactMobileQuizLayout
                ? 'grid place-items-center py-3 md:py-12'
                : 'flex flex-col items-center'
          }`}
          style={{
            maskImage: resolvedEdgeMask,
            WebkitMaskImage: resolvedEdgeMask,
            minHeight: `calc(100dvh - ${
              usesCompactMobileLayout ? QUESTION_STAGE_OFFSET_PX : QUESTION_STAGE_FLEX_OFFSET_PX
            }px)`,
          }}
        >
          {usesFlexibleStageGap && (
            <div aria-hidden="true" data-testid="flow-stage-top-gap" className="min-h-[1.5rem] w-full flex-1 md:min-h-[3rem]" />
          )}
          <AnimatePresence
            initial={false}
            custom={transitionDirection}
            mode="popLayout"
          >
            {isAtFlowEnd ? (
              <motion.div
                key="flow-completed"
                data-testid="flow-completed-stage-transition"
                onAnimationComplete={() => document.dispatchEvent(new Event("theme-layout-change"))}
                data-flow-stage-key="flow-completed"
                data-flow-stage-active={isAtFlowEnd ? "true" : "false"}
                custom={transitionDirection}
                variants={
                  prefersReducedMotion
                    ? REDUCED_MOTION_VARIANTS
                    : themeCardMotion?.variants ?? FLOW_COMPLETED_VARIANTS
                }
                initial="enter"
                animate={prefersReducedMotion ? reducedMotionControls : 'center'}
                exit="exit"
                transition={
                  prefersReducedMotion
                    ? REDUCED_MOTION_TRANSITION
                    : themeCardMotion?.transition ?? FLOW_COMPLETED_TRANSITION
                }
                style={themeCardMotion?.style}
                className={`w-full ${usesCompactMobileLayout ? 'col-start-1 row-start-1 h-full' : 'shrink-0'}`}
              >
                <div
                  className={`flex h-full w-full items-center justify-center ${mobileCardBottomPadding}`}
                  data-testid="flow-completed-stage-container"
                  data-flow-stage-scroll-compensation
                >
                  {completionContent ?? (flow ? (
                    <FlowCompleted flow={flow} completionContext={completionContext} courseId={courseId} />
                  ) : null)}
                </div>
              </motion.div>
            ) : (
              question && (
                <motion.div
                  key={questionId ?? currentQuestionIndex ?? questionKey}
                  data-testid="question-stage-transition"
                  onAnimationComplete={() => document.dispatchEvent(new Event("theme-layout-change"))}
                  data-flow-stage-key={renderedStageKey ?? undefined}
                  data-flow-stage-active={!isAtFlowEnd ? "true" : "false"}
                  custom={transitionDirection}
                  variants={resolvedQuestionCardVariants}
                  initial="enter"
                  animate={prefersReducedMotion ? reducedMotionControls : 'center'}
                  exit="exit"
                  transition={resolvedQuestionCardTransition}
                  style={themeCardMotion?.style}
                  className={`w-full ${usesCompactMobileLayout ? 'col-start-1 row-start-1' : 'shrink-0'}`}
                >
                  <div
                    data-testid="question-stage-card-container"
                    data-flow-stage-scroll-compensation
                    className={`relative flex w-full justify-center ${mobileCardBottomPadding}`}
                  >
                    {canGoToPrevious && usesCardBackTab && (
                      <button
                        type="button"
                        onClick={onPrevious}
                        disabled={isInteractionLocked}
                        aria-label="Previous question"
                        data-testid="question-previous-tab"
                        className="absolute -top-3.5 left-[1.125rem] z-20 inline-flex h-7 items-center gap-0.5 rounded-full bg-white pl-1.5 pr-2.5 text-xs font-semibold text-foundation-base-darkbase-dark-500 shadow-elevation-sm transition-colors hover:bg-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(253,67,25,0.38)] disabled:opacity-50"
                      >
                        <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                        Back
                      </button>
                    )}
                    {recoveryContent ?? (questionError ? <div data-flow-surface="card" className="w-full max-w-xl rounded-3xl border border-border/40 bg-white/80 p-8" aria-live="polite">
                      <p role="alert">{questionError}</p>
                      <Button className="mt-4" variant="secondary" onClick={onRetryQuestion}>Try again</Button>
                    </div> : questionLoading ? <QuestionLoadingSkeleton question={question} /> : <fieldset disabled={isFormLocked} className="contents" aria-busy={isFormLocked}
                      onClickCapture={(event) => { if (isFormLocked) { event.preventDefault(); event.stopPropagation(); } }}
                      onKeyDownCapture={(event) => { if (isFormLocked) { event.preventDefault(); event.stopPropagation(); } }}>
                    <QuestionComponent
                      key={`${flowRunId}-${questionFlowItem?.id ?? question.id}-${generationId ?? ''}`}
                      flowName={flow?.name ?? ''}
                      generationId={generationId}
                      onBusyChange={setEvaluationPending}
                      onGenerationConflict={onGenerationConflict}
                      onDraftChange={handleDraftChange}
                      onServerResponses={onServerResponses}
                      question={question}
                      response={questionResponse}
                      afterResponse={submissionError ? <p role="alert" className="text-sm text-destructive">{submissionError}</p> : undefined}
                      isQuizFlow={Boolean(flow?.is_quiz)}
                      flowItem={questionFlowItem}
                      flowRunId={flowRunId}
                      onSubmit={generationId ? (response, options) => onQuestionSubmit({ ...response, generation_id: generationId }, options) : onQuestionSubmit}
                      isSubmitting={isSubmitting}
                      isInteractionLocked={isInteractionLocked}
                      disableAutoSync={isSavingResponse}
                      isSubmitLoading={isSavingResponse}
                      isLastQuestion={isLastQuestion}
                      lastQuestionButtonText={lastQuestionButtonText ?? (courseId != null ? 'Finish lesson' : undefined)}
                    /></fieldset>)}
                  </div>
                </motion.div>
              )
            )}
          </AnimatePresence>
          {usesFlexibleStageGap && (
            <div aria-hidden="true" data-testid="flow-stage-bottom-gap" className="min-h-[6rem] w-full flex-1" />
          )}
        </div>
      </div>

      {showAccountTools && !isAtFlowEnd && question ? (
        <TooltipProvider delayDuration={150}>
          <div
            className="fixed bottom-[calc(env(safe-area-inset-bottom)+2.25rem)] right-3 z-[400] flex items-center gap-3 md:bottom-6 md:right-6 md:gap-4"
            data-testid="question-flow-floating-tools"
          >
            {bottomRightAccessory}
            {showNarrationControls ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    type="button"
                    size="icon"
                    className="z-[400] h-12 w-12 rounded-full bg-white/40 backdrop-blur-sm backdrop-brightness-[110%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] hover:bg-white transition-colors shadow-elevation-sm border border-solid border-white/10 disabled:opacity-40 disabled:cursor-not-allowed shrink-0 [&_svg]:!size-6"
                    aria-label={narration.isEnabled ? 'Turn narration off' : 'Turn narration on'}
                    aria-pressed={narration.isEnabled}
                    data-testid="question-narration-button"
                    data-tour-id="question-flow-control-narration"
                    onClick={() => {
                      void narration.toggleEnabled(!narration.isEnabled);
                    }}
                    disabled={narration.isSavingSetting}
                  >
                    {narration.isSavingSetting || (narration.isEnabled && narration.isLoading) ? (
                      <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin text-foundation-base-darkbase-dark-500" strokeWidth={1}/>
                    ) : narration.isEnabled ? (
                      <Volume2 aria-hidden="true" className="h-6 w-6 text-foundation-base-darkbase-dark-500" strokeWidth={1}/>
                    ) : (
                      <VolumeOff aria-hidden="true" className="h-6 w-6 text-foundation-base-darkbase-dark-500" strokeWidth={1}/>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {narration.isEnabled ? 'Turn narration off' : 'Turn narration on'}
                </TooltipContent>
              </Tooltip>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <GenerateNoteFromFlowButton
                  question={question}
                  flow={flow}
                  isSubmitting={isInteractionLocked}
                  className="shrink-0"
                />
              </TooltipTrigger>
              <TooltipContent side="top">Create a Note</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <QuestionFeedbackButton
                  question={question}
                  response={questionResponse}
                  isSubmitting={isInteractionLocked}
                />
              </TooltipTrigger>
              <TooltipContent side="top">Submit Feedback</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ) : null}

      {debugMetadata ? (
        <DebugPanel
          className="fixed bottom-32 right-6 z-[450] max-w-sm space-y-3"
          title="Module Debug"
        >
          <div className="space-y-1">
            <div className="font-semibold uppercase text-orange-700">Module</div>
            <DebugField label="flow.name" value={debugMetadata.flowName} />
            <DebugField label="flow.id" value={debugMetadata.flowId} />
            <DebugField label="flow.frequency" value={debugMetadata.flowFrequency} />
            <DebugField label="flow.runId" value={debugMetadata.flowRunId} />
            <DebugField label="flow.status" value={debugMetadata.flowStatus} />
            <DebugField label="flow.completed_at" value={debugMetadata.flowCompletedAt} />
            <DebugField label="progress.index" value={debugMetadata.progressIndex} />
            <DebugField label="progress.total" value={debugMetadata.totalQuestions} />
          </div>
          <div className="space-y-1 border-t border-orange-500/30 pt-2">
            <div className="font-semibold uppercase text-orange-700">Question</div>
            <DebugField label="question.step" value={debugMetadata.questionStep} />
            <DebugField label="question.index" value={debugMetadata.questionIndex} />
            <DebugField label="question.id" value={debugMetadata.questionId} />
            <DebugField label="question.key" value={debugMetadata.questionKey} />
            <DebugField label="question.type" value={debugMetadata.questionType} />
            <DebugField
              label="question.type_display"
              value={debugMetadata.questionTypeDisplay}
            />
            <DebugField label="flow_item.id" value={debugMetadata.flowItemId} />
          </div>
          <div className="space-y-1 border-t border-orange-500/30 pt-2">
            <div className="font-semibold uppercase text-orange-700">State</div>
            <DebugField label="isSubmitting" value={debugMetadata.isSubmitting} />
            <DebugField label="hasUnsavedChanges" value={debugMetadata.hasUnsavedChanges} />
            <DebugField label="draft" value={debugMetadata.draft} />
          </div>
        </DebugPanel>
      ) : null}
    </>
  );
};
